import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Label,
  Button,
  Form,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { get, put, post } from "../../helpers/api_helper";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Trans } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  SHOW_PARTNER_BRANCH,
  UPDATE_PARTNER_BRANCH,
  PARTNERS_SEARCH,
  ITEMS_SEARCH,
  ADDRESS_LIST,
  TIMESLOT_LIST,
  LANGUAGES_SEARCH,
  SEARCH_TIMESLOT
} from "helpers/api_endpoints";
import Select from "react-select";
import AddressForm from "../../components/Common/AddressForm";

document.title = "Edit Partner Branch";

const EditPartnerBranch = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const [partnerId, setPartnerId] = useState(0);
  const [itemId, setItemId] = useState(0);
  const [name, setName] = useState('{"title":[{"1":""}]}');
  const [named, setNamed] = useState({ title: [] });
  const [pickupLocation, setPickupLocation] = useState(0);
  const [preparationTime, setPreparationTime] = useState(30);
  const [deliveryTime, setDeliveryTime] = useState(30);
  const [status, setStatus] = useState(1);
  const [regularDeliveryAddresses, setRegularDeliveryAddresses] = useState([]);
  const [expressDeliveryAddresses, setExpressDeliveryAddresses] = useState([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [bookedAssignmentTime, setBookedAssignmentTime] = useState(0); 

  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isPAddressModalOpen, setIsPAddressModalOpen] = useState(false);
  const [isCoverageAreaAddress, setIsCoverageAreaAddress] = useState(false);
  const [addressTarget, setAddressTarget] = useState(null);

  const [partners, setPartners] = useState([]);
  const [items, setItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [languages, setLanguages] = useState([]);
	const user = JSON.parse(localStorage.getItem('authUser')).user;

  const fetchPartners = async () => {
    try {
      const response = await post(PARTNERS_SEARCH, user.partner?{id:user.partner.id}:{})
      const formattedPartners = response.data.map(el => ({
        value: el.id,
        label: el.name,
      }));
      setPartners(formattedPartners);
      if(formattedPartners.length == 1)
        setPartnerId(formattedPartners[0].value);
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || "Failed to fetch partners");
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await post(LANGUAGES_SEARCH);
      setLanguages(response.data);
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || "Failed to fetch languages");
    }
  };

  const fetchItems = async () => {
    try {
      const response = await post(ITEMS_SEARCH);
      const formattedItems = response.data.map(el => ({
        value: el.id,
        label: el.name,
      }));
      setItems(formattedItems);
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || "Failed to fetch items");
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await get(ADDRESS_LIST);
      const formattedAddresses = response.data.map(el => ({
        value: el.id,
        label: [
          el.title,
          el.street,
          el.flatNo && `Flat ${el.flatNo}`,
          el.subDistrict?.nameTranslated || el.subDistrict?.name,
          el.district?.nameTranslated || el.district?.name,
          el.city?.nameTranslated || el.city?.name,
          el.country?.nameTranslated || el.country?.name,
        ].filter(Boolean).join(', '),
        data: el
      }));
      setAddresses(formattedAddresses);
      return formattedAddresses;
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || "Failed to fetch addresses");
      return [];
    }
  };

  const fetchTimeSlots = async () => {
    try {
      const response = await post(SEARCH_TIMESLOT);
      const formattedTimeSlots = response.data.map(slot => {
        let label = slot.name;
  
        if (slot.TimeslotDetails?.length > 0) {
          const details = slot.TimeslotDetails.map(d => `${d.timeFrom} - ${d.timeTo}`).join(", ");
          label += ` (${details})`;
        }
  
        return {
          value: slot.id,
          label,
          data: slot,
        };
      });
      setTimeSlots(formattedTimeSlots);
      return formattedTimeSlots;
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || "Failed to fetch time slots");
      return [];
    }
  };

  const isOverlap = (slotA, slotB) => {
    const parseTime = timeStr => {
      const [hours, minutes, seconds] = timeStr.split(":").map(Number);
      return hours * 60 + minutes + (seconds || 0);
    };

    return slotA.TimeslotDetails?.some(detailA =>
      slotB.TimeslotDetails.some(detailB => {
        const startA = parseTime(detailA.timeFrom);
        const endA = parseTime(detailA.timeTo);
        const startB = parseTime(detailB.timeFrom);
        const endB = parseTime(detailB.timeTo);
        return startA < endB && startB < endA; // Overlap
      })
    );
  };

  const handleName = (languageId, value) => {
    // Create a deep copy of the current named state
    const updatedNamed = JSON.parse(JSON.stringify(named));
    
    // Ensure title array exists
    if (!updatedNamed.title) {
      updatedNamed.title = [];
    }
    
    // Find if this language already exists
    const langIndex = updatedNamed.title.findIndex(item => item[languageId] !== undefined);
    
    if (langIndex >= 0) {
      // Update existing language entry
      updatedNamed.title[langIndex][languageId] = value;
    } else {
      // Add new language entry
      updatedNamed.title.push({ [languageId]: value });
    }
    
    // Update both states
    setNamed(updatedNamed);
    setName(JSON.stringify(updatedNamed));
  };

  const handleAddressSelection = (type, selectedOptions) => {
    const selectedAddresses = selectedOptions.map(option => option.data);
    
    if (type === "regular") {
      setRegularDeliveryAddresses(selectedAddresses);
    } else {
      setExpressDeliveryAddresses(selectedAddresses);
    }
  };

  const customFilterOption = (option, inputValue) => {
    const search = inputValue.toLowerCase();
    const addressData = option.data;
  
    const searchableText = [
      option.label,
      addressData?.title,
      addressData?.street,
      addressData?.flatNo,
      addressData?.subDistrict?.nameTranslated || addressData?.subDistrict?.name,
      addressData?.district?.nameTranslated || addressData?.district?.name,
      addressData?.city?.nameTranslated || addressData?.city?.name,
      addressData?.country?.nameTranslated || addressData?.country?.name,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
  
    return searchableText.includes(search);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        await Promise.all([
          fetchLanguages(),
          fetchPartners(),
          fetchItems()
        ]);
        
        // These are the options we fetch first
        const [addressOptions, timeSlotOptions] = await Promise.all([
          fetchAddresses(),
          fetchTimeSlots()
        ]);
        
        // Then we pass them to fetchBranchData
        await fetchBranchData(addressOptions, timeSlotOptions);
        
        setInitialLoadComplete(true);
      } catch (error) {
        console.error("Error loading initial data:", error);
        setErrorMsg("Failed to load initial data");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [id]);

  useEffect(() => {
    if (isAddressModalOpen === false) {
      fetchAddresses();
    }
  }, [isAddressModalOpen]);

  const fetchBranchData = async (addressOptions, timeSlotOptions) => {
    setLoading(true);
    try {
      const response = await get(`${SHOW_PARTNER_BRANCH}/${id}`);
      const branchData = response.data;
      
      setPartnerId(branchData.partnerId || 0);
      setItemId(branchData.itemId || 0);
      setBookedAssignmentTime(branchData.bookedAssignmentTime || 0);
      
      // Handle name and named from API
      if (branchData.named) {
        setNamed(branchData.named);
        setName(JSON.stringify(branchData.named));
      } else if (branchData.name) {
        try {
          const parsedName = JSON.parse(branchData.name);
          setNamed(parsedName);
          setName(branchData.name);
        } catch {
          const newNamed = {
            title: [{ 1: branchData.name }]
          };
          setNamed(newNamed);
          setName(JSON.stringify(newNamed));
        }
      }
      
      // Set pickup location
      setPickupLocation(branchData.pickupLocation);
      
      // Set preparation and delivery times
      setPreparationTime(branchData.preparationTime || 30);
      setDeliveryTime(branchData.deliveryTime || 30);
      setStatus(branchData.status || 1);
      
      // Set time slots
      if (branchData.timeSlot && timeSlotOptions) {
        const selectedSlots = timeSlotOptions.filter(slot => 
          branchData.timeSlot.find(el => el.id == slot.value)
        );
        setSelectedTimeSlots(selectedSlots);
      }
      
      // Set regular delivery addresses
      if (branchData.regularServiceArea && addressOptions) {
        const regularAddrs = addressOptions.filter(addr => 
          branchData.regularServiceArea.find(el => el.id == addr.value)
        );
        setRegularDeliveryAddresses(regularAddrs.map(addr => addr.data));
      }
      
      // Set express delivery addresses
      if (branchData.expressServiceArea && addressOptions) {
        const expressAddrs = addressOptions.filter(addr => 
          branchData.expressServiceArea.find(el => el.id == addr.value)
        );
        setExpressDeliveryAddresses(expressAddrs.map(addr => addr.data));
      }
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || "Failed to fetch branch data");
      console.error("Error fetching branch data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFilter = (type, value, ev = "") => {
    if (!value && value !== 0) return;

    switch (type) {
      case "partnerId":
        setPartnerId(value);
        break;
      case "itemId":
        setItemId(value);
        break;
      case "pickupLocation":
        setPickupLocation(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
  
    try {
      // Validate
      if (!partnerId) {
        setErrorMsg("Please select a valid partner before proceeding.");
        return;
      }
  
      if (!itemId) {
        setErrorMsg("Please select a valid item before proceeding.");
        return;
      }
  
      // Ensure at least one language is filled (language 1 or 2)
      const hasRequiredLanguage = named.title?.some(item => 
        item[1]?.trim() || item[2]?.trim()
      );
      
      if (!hasRequiredLanguage) {
        setErrorMsg("Please enter a name in at least one required language (1 or 2)");
        return;
      }
      
      const branchData = {
        id: Number(id),
        partnerId: Number(partnerId),
        itemId: Number(itemId),
        name: JSON.stringify(named), // Use the stringified named object
        named, // Also include the named object directly
        symbol: "PARTNERBRANCH",
        groupId: 0,
        bookedAssignmentTime,
        status: Number(status),
        timeSlots: selectedTimeSlots.map(slot => slot.value),
        regularDeliveryAddresses: regularDeliveryAddresses.map(addr => addr.id),
        expressDeliveryAddresses: expressDeliveryAddresses.map(addr => addr.id),
        pickupLocation: Number(pickupLocation),
        preparationTime: Number(preparationTime),
        deliveryTime: Number(deliveryTime),
      };
  
      const response = await put(UPDATE_PARTNER_BRANCH, branchData);
  
      if (response.code === 200) {
        navigate(`/partnersbranch`, {
          state: { successMsg: "Partner Branch Updated Successfully" },
        });
      }
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.message || "Failed to update partner branch"
      );
      console.error("Error updating partner branch:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !initialLoadComplete) {
    return <div className="text-center py-5">Loading...</div>;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Partner Branches" breadcrumbItem="Edit Branch" />

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col xl={12}>
                <Card>
                  <CardBody>
                    {errorMsg && <Alert color="danger">{errorMsg}</Alert>}
                    {successMsg && <Alert color="success">{successMsg}</Alert>}

                    <Row>
                      <div className="form-group col-md-6 mb-2">
                        <Label className="form-label">
                          <Trans>Partner</Trans>
                        </Label>
                        <Select
                          name="partnerId"
                          options={partners}
                          value={partners.filter(option => {
                            return partnerId == option.value
                          })}
                          className="form-control select2"
                          onChange={e => handleFilter("partnerId", e.value)}
                        />
                      </div>

                      <div className="form-group col-md-6 mb-2">
                        <Label className="form-label">
                          <Trans>Partner Item</Trans>
                        </Label>
                        <Select
                          name="itemId"
                          options={items}
                          value={items.filter(option => {
                            return itemId == option.value
                          })}
                          className="form-control select2"
                          onChange={e => handleFilter("itemId", e.value)}
                        />
                      </div>

                      <div className="form-group col-md-6 mb-2">
                        <Label className="form-label">
                          <Trans>Partner Time Slot</Trans>
                        </Label>
                        <Select
                          name="timeslotIds"
                          options={timeSlots}
                          isMulti
                          value={selectedTimeSlots}
                          className="form-control select2"
                          onChange={selectedOptions => {
                            // If nothing is selected, just update and return
                            if (!selectedOptions || selectedOptions.length === 0) {
                              setSelectedTimeSlots([]);
                              return;
                            }
                          
                            // Identify if this is an add or remove action
                            const newSlot = selectedOptions.find(
                              opt => !selectedTimeSlots.some(sel => sel.value === opt.value)
                            );
                          
                            // If removing (no newSlot), just update state
                            if (!newSlot) {
                              setSelectedTimeSlots(selectedOptions);
                              return;
                            }
                          
                            // Check overlap only when adding
                            const alreadySelected = selectedTimeSlots.map(slot => slot.data);
                          
                            const hasConflict = alreadySelected.some(existing =>
                              isOverlap(existing, newSlot.data)
                            );
                          
                            if (hasConflict) {
                              alert("Selected timeslot overlaps with an existing one. Please choose a different timeslot.");
                              return;
                            }
                          
                            setSelectedTimeSlots(selectedOptions);
                          }}
                          placeholder="Select one or more time slots"
                          closeMenuOnSelect={false}
                        />
                      </div>

                      <Row className="my-3">
                        {languages.map((language, index) => {
                          // Find the value for this language
                          const langValue = named.title?.find(item => item[language.id])?.[language.id] || "";
                          
                          return (
                            <div className="form-group col-md-3 mt-2" key={index}>
                              <Label className="form-label">
                                <Trans>Name ({language.name})</Trans>
                              </Label>
                              <Input
                                name={`name_${language.id}`}
                                value={langValue}
                                className="form-control"
                                placeholder="Enter Name"
                                type="text"
                                onChange={e => handleName(language.id, e.target.value)}
                                required={language.id === 1 || language.id === 2}
                              />
                            </div>
                          );
                        })}
                      </Row>

                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label className="form-label">
                              <Trans>Regular Delivery Coverage Areas</Trans>
                            </Label>
                            <Select
                              isMulti
                              name="regularDeliveryAddresses"
                              options={addresses}
                              value={addresses.filter(option =>
                                regularDeliveryAddresses.some(
                                  addr => addr.id === option.value
                                )
                              )}
                              className="basic-multi-select"
                              classNamePrefix="select"
                              onChange={selected =>
                                handleAddressSelection("regular", selected)
                              }
                              closeMenuOnSelect={false}
                              placeholder="Select addresses for regular delivery..."
                              filterOption={customFilterOption}
                            />
                            <Button
                              onClick={() => {
                                setIsAddressModalOpen(true);
                                setAddressTarget("regular");
                                setIsCoverageAreaAddress(true);
                              }}
                              color="danger"
                              className="mt-2"
                            >
                              Create District +
                            </Button>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-3">
                            <Label className="form-label">
                              <Trans>Express Delivery Coverage Areas</Trans>
                            </Label>
                            <Select
                              isMulti
                              name="expressDeliveryAddresses"
                              options={addresses}
                              value={addresses.filter(option =>
                                expressDeliveryAddresses.some(
                                  addr => addr.id === option.value
                                )
                              )}
                              className="basic-multi-select"
                              classNamePrefix="select"
                              onChange={selected =>
                                handleAddressSelection("express", selected)
                              }
                              closeMenuOnSelect={false}
                              placeholder="Select addresses for express delivery..."
                              filterOption={customFilterOption}
                            />
                            <Button
                              onClick={() => {
                                setIsAddressModalOpen(true);
                                setAddressTarget("express");
                                setIsCoverageAreaAddress(true);
                              }}
                              color="danger"
                              className="mt-2"
                            >
                              Create District +
                            </Button>
                          </div>
                        </Col>
                      </Row>

                      <div className="form-group mb-3 col-md-3">
                        <Label>
                          <Trans>Preparation Time (minutes)</Trans>
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          value={preparationTime}
                          onChange={e => setPreparationTime(e.target.value)}
                          required
                        />
                      </div>

                      <div className="form-group mb-3 col-md-3">
                        <Label>
                          <Trans>Delivery Time (minutes)</Trans>
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          value={deliveryTime}
                          onChange={e => setDeliveryTime(e.target.value)}
                          required
                        />
                      </div>

                      <div className="form-group mb-3 col-md-3">
                        <Label>
                          <Trans>Booking Assignment Time (minutes)</Trans>
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          value={bookedAssignmentTime}
                          onChange={e => setBookedAssignmentTime(e.target.value)}
                          required
                        />
                      </div>

                      <div className="form-group mb-3 col-md-3">
                        <Label>
                          <Trans>Status</Trans>
                        </Label>
                        <Input
                          type="select"
                          value={status}
                          onChange={e => setStatus(parseInt(e.target.value))}
                          required
                        >
                          <option value={1}>Active</option>
                          <option value={2}>Inactive</option>
                        </Input>
                      </div>

                      <div className="form-group mb-3">
                        <Label>
                          <Trans>Pickup Location (Full Address)</Trans>
                        </Label>
                        <Select
                          name="pickupLocation"
                          options={addresses}
                          value={addresses.filter(option => {
                            return pickupLocation == option.value
                          })}
                          className="form-control select2"
                          onChange={e => handleFilter("pickupLocation", e.value)}
                          filterOption={customFilterOption}
                        />
                        <Button
                          onClick={() => {
                            setIsPAddressModalOpen(true);
                          }}
                          color="danger"
                          className="mt-2"
                        >
                          Create Address +
                        </Button>
                      </div>
                    </Row>

                    <div className="mt-4">
                      <Button type="submit" color="primary" disabled={loading}>
                        {loading ? "Processing..." : "Update"}
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>

          <Modal
            isOpen={isAddressModalOpen}
            toggle={() => setIsAddressModalOpen(!isAddressModalOpen)}
            zIndex={900}
          >
            <ModalHeader toggle={() => setIsAddressModalOpen(false)}>
              Create New District
            </ModalHeader>
            <ModalBody>
              <AddressForm
                onSuccess={createdAddress => {
                  setIsAddressModalOpen(false);

                  const formatted = {
                    value: createdAddress.id,
                    label: [
                      createdAddress.title,
                      createdAddress.street,
                      createdAddress.flatNo && `Flat ${createdAddress.flatNo}`,
                      createdAddress.subDistrict?.nameTranslated ||
                        createdAddress.subDistrict?.name,
                      createdAddress.district?.nameTranslated ||
                        createdAddress.district?.name,
                      createdAddress.city?.nameTranslated ||
                        createdAddress.city?.name,
                      createdAddress.country?.nameTranslated ||
                        createdAddress.country?.name,
                    ]
                      .filter(Boolean)
                      .join(", "),
                    data: createdAddress,
                  };

                  setAddresses(prev => [...prev, formatted]);

                  if (addressTarget === "pickup") {
                    setPickupLocation(createdAddress.id);
                  } else if (addressTarget === "regular") {
                    setRegularDeliveryAddresses(prev => [
                      ...prev,
                      formatted.data,
                    ]);
                  } else if (addressTarget === "express") {
                    setExpressDeliveryAddresses(prev => [
                      ...prev,
                      formatted.data,
                    ]);
                  }

                  setSuccessMsg("Address created successfully!");
                  setTimeout(() => setSuccessMsg(""), 5000);
                }}
                onCancel={() => setIsAddressModalOpen(false)}
                isAddressForCoverageArea={isCoverageAreaAddress}
              />
            </ModalBody>
          </Modal>
          <Modal
            isOpen={isPAddressModalOpen}
            toggle={() => setIsPAddressModalOpen(!isPAddressModalOpen)}
            size="lg"
            zIndex={900}
          >
            <ModalHeader toggle={() => setIsPAddressModalOpen(false)}>
              Create New District
            </ModalHeader>
            <ModalBody>
              <AddressForm mode={1} showMap={true} addressId={pickupLocation} express={true}
                onSuccess={(data)=>{setPickupLocation(data.id);setIsPAddressModalOpen(false);const temp = [...addresses];temp.push({value: data.id, label: data.title, data: data});setAddresses(temp);}}
                onCancel={() => setIsPAddressModalOpen(false)}
              />
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  );
};

export default EditPartnerBranch;